// ============================================================================
// PsyReportsPanel — ОТЧЁТЫ ПО МЕТОДИКАМ и печать «Заключение по тесту»
// (модуль hydra.medic.psy, ГидраМедик).
//
// Один экран, мобильная вёрстка БЕЗ таблиц и модальных окон:
//   • список отчётов по методикам (карточки; отчёт сопоставлен методике по
//     имени — детерминированный ключ поставки psy);
//   • отборы: методика, пациент, период (применяются к строкам отчёта и к
//     протоколам; если в отчёте нет колонки даты/клиента — честная подпись,
//     отбор остаётся на протоколах);
//   • просмотр отчёта — строки карточками (PsyReportView);
//   • печать — по записи протокола (PsyReportRecords, серверный рендер);
//   • явные состояния: загрузка (в т.ч. «долго»), ошибка (+ «Повторить»),
//     пусто, нет прав, частичный сбой, нет печатной формы.
//
// Все данные — существующие RPC (SSoT сервера), новых механизмов нет.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ChevronLeft, FileBarChart2, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import {
  loadPsyPatients, loadPsyProtocols, loadPsyReportCatalog, loadPsyReportResult,
  type PsyPatientOption, type PsyProtocolsLoad,
  type PsyReportCatalog, type PsyReportEntry, type PsyReportResult,
} from '@/lib/psyReports';
import { filterPsyProtocols, filterPsyReportResult } from '@/lib/psyReportsFilter';
import { PsyReportsFilters } from '@/components/psy/PsyReportsFilters';
import { PsyReportSection } from '@/components/psy/PsyReportSection';
import { PsyReportsList } from '@/components/psy/PsyReportsList';
import { PsyReportRecords } from '@/components/psy/PsyReportRecords';

export interface PsyReportsPanelProps {
  /** Предвыбранная методика (код типа опроса) — при открытии из теста. */
  initialMethodCode?: string;
  /** Предвыбранный пациент (из рабочего места психолога). */
  patientId?: string;
  patientName?: string;
  /** Назад к тестам (если панель открыта поверх списка методик). */
  onBack?: () => void;
}

interface ReportState {
  loading: boolean;
  error: string;
  result: PsyReportResult | null;
}

interface ProtocolState extends PsyProtocolsLoad {
  loading: boolean;
  error: string;
}

const EMPTY_PROTOCOLS: PsyProtocolsLoad = { items: [], denied: false, tableMissing: false };
const SLOW_LOAD_MS = 12000;

function useSlowFlag(loading: boolean): boolean {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!loading) { setSlow(false); return; }
    const timer = setTimeout(() => setSlow(true), SLOW_LOAD_MS);
    return () => clearTimeout(timer);
  }, [loading]);
  return slow;
}

export function PsyReportsPanel({
  initialMethodCode = '', patientId: patientIdProp = '', patientName: patientNameProp = '', onBack,
}: PsyReportsPanelProps) {
  const [catalog, setCatalog] = useState<PsyReportCatalog | null>(null);
  const [patients, setPatients] = useState<PsyPatientOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [patientWarning, setPatientWarning] = useState('');
  const [methodCode, setMethodCode] = useState(initialMethodCode);
  const [patientId, setPatientId] = useState(patientIdProp);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [openKey, setOpenKey] = useState('');
  const [reportState, setReportState] = useState<ReportState>({ loading: false, error: '', result: null });
  const [protocolState, setProtocolState] = useState<ProtocolState>({ loading: false, error: '', ...EMPTY_PROTOCOLS });
  const catalogSeq = useRef(0);
  const entrySeq = useRef(0);
  const autoOpened = useRef(false);

  const loadCatalog = useCallback(async () => {
    const current = ++catalogSeq.current;
    setLoading(true);
    setError('');
    setPatientWarning('');
    const [cat, pats] = await Promise.allSettled([loadPsyReportCatalog(), loadPsyPatients()]);
    if (catalogSeq.current !== current) return;
    if (cat.status === 'fulfilled') {
      setCatalog(cat.value);
    } else {
      logErrorToJournal(cat.reason, 'psyReports: каталог отчётов');
      setError(friendlyError(cat.reason));
    }
    if (pats.status === 'fulfilled') {
      setPatients(pats.value);
    } else {
      logErrorToJournal(pats.reason, 'psyReports: пациенты для отбора');
      setPatientWarning('Список пациентов недоступен — отбор по пациенту не предлагается.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { void loadCatalog(); }, [loadCatalog]);
  useEffect(() => () => { catalogSeq.current += 1; entrySeq.current += 1; }, []);

  // Пациент выбран в рабочем месте — панель следует за выбором.
  useEffect(() => {
    if (patientIdProp) setPatientId(patientIdProp);
  }, [patientIdProp]);

  const slow = useSlowFlag(loading);
  const reportSlow = useSlowFlag(reportState.loading);

  const openEntry = useMemo(
    () => catalog?.items.find((item) => item.key === openKey) || null,
    [catalog, openKey],
  );

  const loadEntry = useCallback(async (entry: PsyReportEntry) => {
    const current = ++entrySeq.current;
    setReportState({ loading: entry.reportId !== '', error: '', result: null });
    setProtocolState({ loading: entry.methodCode !== '', error: '', ...EMPTY_PROTOCOLS });
    const [reportRes, protocolRes] = await Promise.allSettled([
      entry.reportId ? loadPsyReportResult(entry.reportId) : Promise.resolve(null),
      entry.methodCode ? loadPsyProtocols(entry.methodCode) : Promise.resolve(EMPTY_PROTOCOLS),
    ]);
    if (entrySeq.current !== current) return;
    if (reportRes.status === 'fulfilled') {
      setReportState({ loading: false, error: '', result: reportRes.value });
    } else {
      logErrorToJournal(reportRes.reason, `psyReports: отчёт «${entry.reportName || entry.methodName}»`);
      setReportState({ loading: false, error: friendlyError(reportRes.reason), result: null });
    }
    if (protocolRes.status === 'fulfilled') {
      setProtocolState({ loading: false, error: '', ...protocolRes.value });
    } else {
      logErrorToJournal(protocolRes.reason, `psyReports: протоколы «${entry.methodName}»`);
      setProtocolState({ loading: false, error: friendlyError(protocolRes.reason), ...EMPTY_PROTOCOLS });
    }
  }, []);

  useEffect(() => {
    if (!openEntry) { entrySeq.current += 1; return; }
    void loadEntry(openEntry);
  }, [openEntry, loadEntry]);

  // Открытие из теста: сразу показываем отчёт этой методики.
  useEffect(() => {
    if (!catalog || autoOpened.current) return;
    const wanted = String(initialMethodCode || '').trim();
    if (!wanted) { autoOpened.current = true; return; }
    const found = catalog.items.find((item) => item.methodCode === wanted);
    autoOpened.current = true;
    if (found) {
      setMethodCode(wanted);
      setOpenKey(found.key);
    }
  }, [catalog, initialMethodCode]);

  const patientMap = useMemo(() => new Map(patients.map((p) => [p.id, p.name])), [patients]);
  const filters = useMemo(() => ({
    patientId,
    // Имя — из картотеки; имя из рабочего места — только для ТОГО ЖЕ пациента.
    patientName: patientMap.get(patientId) || (patientId === patientIdProp ? patientNameProp : ''),
    from,
    to,
  }), [patientId, patientIdProp, patientNameProp, patientMap, from, to]);
  const filtersActive = !!(patientId || from || to);

  const filteredReport = useMemo(
    () => (reportState.result ? filterPsyReportResult(reportState.result, filters, patientMap) : null),
    [reportState.result, filters, patientMap],
  );
  const filteredRecords = useMemo(() => {
    const items = filterPsyProtocols(protocolState.items, filters);
    // Представление ссылки может отсутствовать у системного subject_id —
    // подставляем имя из картотеки пациентов (SSoT — серверные данные).
    return items.map((record) => ({
      ...record,
      patientName: record.patientName || patientMap.get(record.patientId) || '',
    }));
  }, [protocolState.items, filters, patientMap]);

  const methodOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of catalog?.items || []) {
      if (item.methodCode && !seen.has(item.methodCode)) seen.set(item.methodCode, item.methodName);
    }
    return [...seen.entries()].map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [catalog]);

  const visibleEntries = useMemo(
    () => (methodCode ? (catalog?.items || []).filter((item) => item.methodCode === methodCode) : (catalog?.items || [])),
    [catalog, methodCode],
  );

  const resetFilters = useCallback(() => {
    setPatientId('');
    setFrom('');
    setTo('');
    setMethodCode('');
  }, []);

  const changeMethod = (value: string) => {
    setMethodCode(value);
    setOpenKey('');
  };

  return (
    <div className="h-full min-h-0 flex flex-col">
      <div className="px-3 py-2 border-b flex items-center gap-2 flex-wrap shrink-0">
        {onBack && (
          <Button size="sm" variant="ghost" className="h-7 px-1.5 text-[11px]" onClick={onBack}>
            <ChevronLeft className="h-3.5 w-3.5" /> К тестам
          </Button>
        )}
        <FileBarChart2 className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">Отчёты по методикам</span>
        {catalog && !catalog.denied && (
          <span className="text-[11px] text-muted-foreground">всего: {catalog.items.length}</span>
        )}
        <Button size="sm" variant="outline" className="h-7 px-2 ml-auto" onClick={() => void loadCatalog()} disabled={loading} title="Обновить список отчётов">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
      </div>

      <PsyReportsFilters
        methodCode={methodCode}
        methodOptions={methodOptions}
        patientId={patientId}
        patientName={filters.patientName}
        patients={patients}
        patientWarning={patientWarning}
        from={from}
        to={to}
        filtersActive={filtersActive}
        filterLabel={filtersActive
          ? `${patientId ? `пациент «${filters.patientName || 'выбран'}»` : 'все пациенты'}${from || to ? ` · период ${from || '…'} — ${to || '…'}` : ''}`
          : ''}
        onChangeMethod={changeMethod}
        onChangePatient={setPatientId}
        onChangeFrom={setFrom}
        onChangeTo={setTo}
        onReset={resetFilters}
      />

      <div className="flex-1 min-h-0 overflow-auto p-3 space-y-3">
        {loading && !catalog && (
          <div className="rounded-lg border px-3 py-2 text-xs space-y-1">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Загрузка отчётов…
            </div>
            {slow && (
              <div className="flex items-center gap-2 text-muted-foreground">
                Это занимает дольше обычного — проверьте подключение.
                <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => void loadCatalog()}>Повторить</Button>
              </div>
            )}
          </div>
        )}

        {error && !loading && (
          <div className="rounded-lg border border-destructive/40 px-3 py-2 space-y-1.5">
            <div className="text-sm font-medium text-destructive">Отчёты не загружены</div>
            <div className="text-xs text-muted-foreground break-words">{error}</div>
            <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => void loadCatalog()}>Повторить</Button>
          </div>
        )}

        {catalog?.denied && (
          <div className="rounded-lg border px-3 py-3 text-xs bg-card">
            <div className="text-sm font-medium">Нет доступа к отчётам</div>
            <div className="text-muted-foreground mt-1">
              Роли не выдано право чтения отчётов — обратитесь к администратору базы.
            </div>
          </div>
        )}

        {catalog && catalog.warnings.length > 0 && !catalog.denied && (
          <div className="rounded-lg border border-amber-500/40 px-3 py-2 text-xs">
            <div className="flex items-center gap-1.5 font-medium">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> Часть данных недоступна
            </div>
            <div className="text-muted-foreground">{catalog.warnings.join(', ')}.</div>
          </div>
        )}

        {catalog && !catalog.denied && !openEntry && (
          <PsyReportsList
            entries={visibleEntries}
            filtersActive={filtersActive}
            onOpen={setOpenKey}
          />
        )}

        {catalog && !catalog.denied && openEntry && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="ghost" className="h-7 px-1.5 text-[11px]" onClick={() => setOpenKey('')}>
                <ChevronLeft className="h-3.5 w-3.5" /> Все отчёты
              </Button>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{openEntry.methodName}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {openEntry.reportName || 'отчёт для методики не найден'}
                </div>
              </div>
            </div>

            <PsyReportSection
              entry={openEntry}
              loading={reportState.loading}
              error={reportState.error}
              result={reportState.result}
              filtered={filteredReport}
              reportSlow={reportSlow}
              filtersActive={filtersActive}
              patientId={patientId}
              from={from}
              to={to}
              onRetry={() => void loadEntry(openEntry)}
              onResetFilters={resetFilters}
            />

            {openEntry.methodCode ? (
              <PsyReportRecords
                entry={openEntry}
                records={filteredRecords}
                loading={protocolState.loading}
                error={protocolState.error}
                denied={protocolState.denied}
                tableMissing={protocolState.tableMissing}
                filtersActive={filtersActive}
                onRetry={() => void loadEntry(openEntry)}
              />
            ) : (
              <div className="rounded-lg border px-3 py-2 text-xs text-muted-foreground bg-card">
                Отчёт не сопоставлен методике (имя отчёта не совпало с синонимом типа опроса) —
                протоколы и печать недоступны.
              </div>
            )}

            {openEntry.printFormId && (
              <div className="text-[10px] text-muted-foreground">
                Печать «Заключение по тесту» выполняется сервером по записи протокола: поля и
                представления подставляет сервер, клиент передаёт только запись.
              </div>
            )}
          </div>
        )}

        {!loading && !catalog && !error && (
          <div className="rounded-lg border px-3 py-3 text-xs bg-card">
            <div className="text-sm font-medium">Отчёты недоступны</div>
            <div className="text-muted-foreground mt-1">Обновите список — данных нет.</div>
          </div>
        )}
      </div>
    </div>
  );
}
